import { useState } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { DollarSign } from "lucide-react";
import { toast } from "sonner";
import { todayLocalISO, daysAgoLocalISO } from "../lib/utils";

/**
 * Shared "Record Payment" / Quick Pay form.
 *
 * Single source of truth for both the Payments page Quick Pay sheet
 * and the Dashboard Record Payment card so the UX stays identical.
 *
 * Props:
 *  - employees: [{ employee_id, name, pay_type, pay_value, balance: { balance } }]
 *  - onSuccess(): called after the payment is recorded (parent should refresh + close)
 *  - onCancel(): called when the user clicks Cancel
 */
const QuickPayForm = ({ employees, onSuccess, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payPeriods, setPayPeriods] = useState([]);
  const [periodSelectValue, setPeriodSelectValue] = useState("");
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [showOlderPeriods, setShowOlderPeriods] = useState(false);

  const [formData, setFormData] = useState({
    employee_id: "",
    pay_period_start: "",
    pay_period_end: "",
    payment_date: todayLocalISO(),
    payment_method: "cash",
    amount_paid: "",
    extra_amount: "0",
    extra_reason: "",
    deduction_amount: "0",
    deduction_reason: "",
    notes: "",
  });

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const dateOnly = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
    return new Date(dateOnly + "T12:00:00").toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const handleEmployeeSelect = async (empId) => {
    setFormData({ ...formData, employee_id: empId });
    const emp = employees.find((e) => e.employee_id === empId);
    setSelectedEmployee(emp);

    setPeriodsLoading(true);
    setPayPeriods([]);
    setPeriodSelectValue("");
    try {
      const res = await apiClient.get(`/employees/${empId}/pay-periods`);
      const periods = res.data.periods || [];
      setPayPeriods(periods);

      // Pre-select most-recent unpaid period and pre-fill amount.
      const unpaid = [...periods].reverse().find((p) => p.balance > 0.01);
      const target = unpaid || periods[periods.length - 1];
      if (target) {
        const key = `${target.period_start}|${target.period_end}`;
        setPeriodSelectValue(key);
        setFormData((prev) => ({
          ...prev,
          employee_id: empId,
          pay_period_start: target.period_start,
          pay_period_end: target.period_end,
          amount_paid: unpaid ? unpaid.balance.toFixed(2) : prev.amount_paid,
        }));
        return;
      }
    } catch (err) {
      console.warn("Could not load pay periods, falling back to date pickers:", err);
    } finally {
      setPeriodsLoading(false);
    }

    // Fallback: legacy 2-week auto-suggest (uses local timezone, not UTC).
    setPeriodSelectValue("__custom__");
    setFormData((prev) => ({
      ...prev,
      employee_id: empId,
      pay_period_start: daysAgoLocalISO(14),
      pay_period_end: todayLocalISO(),
    }));
  };

  const handlePeriodSelect = (value) => {
    setPeriodSelectValue(value);
    if (value === "__custom__") {
      if (!formData.pay_period_start || !formData.pay_period_end) {
        setFormData((prev) => ({
          ...prev,
          pay_period_start: daysAgoLocalISO(14),
          pay_period_end: todayLocalISO(),
        }));
      }
      return;
    }
    const [ps, pe] = value.split("|");
    const period = payPeriods.find((p) => p.period_start === ps && p.period_end === pe);
    setFormData((prev) => ({
      ...prev,
      pay_period_start: ps,
      pay_period_end: pe,
      amount_paid: period && period.balance > 0 ? period.balance.toFixed(2) : prev.amount_paid,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post("/payments", {
        employee_id: formData.employee_id,
        pay_period_start: formData.pay_period_start,
        pay_period_end: formData.pay_period_end,
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        amount_paid: parseFloat(formData.amount_paid),
        extra_amount: parseFloat(formData.extra_amount || 0),
        extra_reason: formData.extra_reason || undefined,
        deduction_amount: parseFloat(formData.deduction_amount || 0),
        deduction_reason: formData.deduction_reason || undefined,
        notes: formData.notes || undefined,
      });
      toast.success("Payment recorded");
      onSuccess?.();
    } catch (error) {
      console.error("Failed to record payment:", error);
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-6">
      <div className="space-y-2">
        <Label>Employee *</Label>
        <Select value={formData.employee_id} onValueChange={handleEmployeeSelect}>
          <SelectTrigger data-testid="payment-employee-select">
            <SelectValue placeholder="Select employee" />
          </SelectTrigger>
          <SelectContent>
            {employees.map((emp) => (
              <SelectItem key={emp.employee_id} value={emp.employee_id}>
                <div className="flex items-center justify-between w-full">
                  <span>{emp.name}</span>
                  {emp.balance?.balance > 0 && (
                    <span className="text-[#E11D48] text-sm ml-2">
                      {formatCurrency(emp.balance.balance)}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedEmployee && (
        <div className="p-4 bg-slate-50 rounded-lg space-y-2">
          <div className="flex justify-between">
            <span className="text-slate-600">Outstanding Balance:</span>
            <span className={`font-semibold ${
              selectedEmployee.balance?.balance > 0 ? "text-[#E11D48]" : "text-[#10B981]"
            }`}>
              {formatCurrency(selectedEmployee.balance?.balance || 0)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Pay Type:</span>
            <span className="capitalize">{selectedEmployee.pay_type}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Rate:</span>
            <span>{formatCurrency(selectedEmployee.pay_value)}</span>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Pay Period *</Label>
        {periodsLoading ? (
          <div className="text-xs text-slate-500 py-2">Loading available periods…</div>
        ) : payPeriods.length > 0 ? (
          <Select value={periodSelectValue} onValueChange={handlePeriodSelect}>
            <SelectTrigger data-testid="payment-period-select">
              <SelectValue placeholder="Select a pay period" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              {(() => {
                const reversed = [...payPeriods].reverse();
                const visible = showOlderPeriods
                  ? reversed
                  : reversed.filter((p, idx) => idx < 24 || p.balance > 0.01);
                return visible.map((p) => {
                  const key = `${p.period_start}|${p.period_end}`;
                  const isPaid = p.balance <= 0.01;
                  const status = isPaid ? "Paid" : `${formatCurrency(p.balance)} unpaid`;
                  return (
                    <SelectItem key={key} value={key} data-testid={`period-option-${p.period_start}`}>
                      <span className="flex items-center justify-between gap-3 w-full">
                        <span className="font-medium">
                          {formatDate(p.period_start)} – {formatDate(p.period_end)}
                        </span>
                        <span className={`text-xs ${isPaid ? "text-slate-400" : "text-amber-600 font-semibold"}`}>
                          {status}
                        </span>
                      </span>
                    </SelectItem>
                  );
                });
              })()}
              <SelectItem value="__custom__" data-testid="period-option-custom">
                <span className="text-slate-600 italic">Other / custom dates…</span>
              </SelectItem>
            </SelectContent>
          </Select>
        ) : !formData.employee_id ? (
          <div className="text-xs text-slate-500 py-2" data-testid="period-empty-hint">
            Select an employee first to load their pay periods.
          </div>
        ) : (
          <div className="text-xs text-slate-500 py-2">No pay periods available — pick custom dates below.</div>
        )}
        {payPeriods.length > 24 && periodSelectValue !== "__custom__" && (
          <button
            type="button"
            onClick={() => setShowOlderPeriods((s) => !s)}
            className="text-xs text-[#0D9488] hover:underline"
            data-testid="toggle-older-periods-btn"
          >
            {showOlderPeriods ? "Hide older paid periods" : "Show older paid periods"}
          </button>
        )}
      </div>

      {(periodSelectValue === "__custom__" ||
        (payPeriods.length === 0 && !periodsLoading && formData.employee_id)) && (
        <div className="grid grid-cols-2 gap-4 p-3 bg-amber-50 border border-amber-100 rounded-md">
          <div className="space-y-2">
            <Label className="text-xs">Period Start *</Label>
            <Input
              type="date"
              value={formData.pay_period_start}
              onChange={(e) => setFormData({ ...formData, pay_period_start: e.target.value })}
              required
              data-testid="payment-period-start"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Period End *</Label>
            <Input
              type="date"
              value={formData.pay_period_end}
              onChange={(e) => setFormData({ ...formData, pay_period_end: e.target.value })}
              required
              data-testid="payment-period-end"
            />
          </div>
          <p className="col-span-2 text-[11px] text-amber-700">
            ⚠️ Custom dates may not align with your pay schedule and won&apos;t appear under their period in the Pay Periods view.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Payment Date *</Label>
          <Input
            type="date"
            value={formData.payment_date}
            onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
            required
            data-testid="payment-date"
          />
        </div>
        <div className="space-y-2">
          <Label>Method *</Label>
          <Select
            value={formData.payment_method}
            onValueChange={(val) => setFormData({ ...formData, payment_method: val })}
          >
            <SelectTrigger data-testid="payment-method-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="zelle">Zelle</SelectItem>
              <SelectItem value="check">Check</SelectItem>
              <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
              <SelectItem value="venmo">Venmo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Amount Paid *</Label>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="number"
            step="0.01"
            min="0"
            value={formData.amount_paid}
            onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
            placeholder="0.00"
            className="pl-9"
            required
            data-testid="payment-amount"
          />
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4">
        <p className="text-sm font-medium text-slate-700 mb-3">Adjustments (Optional)</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#10B981]">Extra Pay</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.extra_amount}
              onChange={(e) => setFormData({ ...formData, extra_amount: e.target.value })}
              placeholder="0.00"
              data-testid="payment-extra"
            />
            <Input
              placeholder="Reason (e.g., Bonus)"
              value={formData.extra_reason}
              onChange={(e) => setFormData({ ...formData, extra_reason: e.target.value })}
              data-testid="payment-extra-reason"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#E11D48]">Deduction</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.deduction_amount}
              onChange={(e) => setFormData({ ...formData, deduction_amount: e.target.value })}
              placeholder="0.00"
              data-testid="payment-deduction"
            />
            <Input
              placeholder="Reason (e.g., Advance)"
              value={formData.deduction_reason}
              onChange={(e) => setFormData({ ...formData, deduction_reason: e.target.value })}
              data-testid="payment-deduction-reason"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Any additional notes..."
          rows={2}
          data-testid="payment-notes"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={saving || !formData.employee_id || !formData.amount_paid}
          className="bg-[#0D9488] hover:bg-[#14B8A6]"
          data-testid="submit-payment-btn"
        >
          {saving ? "Recording..." : "Record Payment"}
        </Button>
      </div>
    </form>
  );
};

export default QuickPayForm;
